import React, { useCallback, useEffect, useMemo, useState } from "react";
import { validator } from "../utils/validator";
import TextField from "../components/common/form/TextField";
import { useDispatch, useSelector } from "react-redux";
import { findUserByEmail, getCurrentUserData } from "../store/users";
import {
  createGroup,
  getDataLoaded,
  getGroupById,
  getGroupByName,
  getGroupsLoadingStatus,
} from "../store/groups";
import { getInvitationById } from "../store/invitations";
import { getTownById } from "../store/towns";
import localStorageService from "../service/http.localStorage";
import { useI18n } from "../i18n/LanguageContext";

const CreateTeamPage = ({ onShowModal }) => {
  const dispatch = useDispatch();
  const { t } = useI18n();

  const currentUser = useSelector(getCurrentUserData());
  const groupsLoaded = useSelector(getDataLoaded());
  const groupsLoading = useSelector(getGroupsLoadingStatus());
  const group = useSelector(getGroupById(currentUser?.teamId));

  const town = useSelector(getTownById(currentUser?.town));

  const [data, setData] = useState({
    teamname: "",
    memberOne: currentUser?._id || "",
    memberTwo: "",
    memberThree: "",
    town: currentUser?.town || "",
  });

  const [errors, setErrors] = useState({});
  const [block, setBlock] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const team = useSelector(getGroupByName(data.teamname));
  const memberTwo = useSelector(findUserByEmail(data.memberTwo));
  const memberThree = useSelector(findUserByEmail(data.memberThree));
  const invitationTwo = useSelector(getInvitationById(memberTwo?._id));
  const invitationThree = useSelector(getInvitationById(memberThree?._id));

  const validatorConfig = useMemo(
    () => ({
      teamname: {
        isRequired: {
          message: t("team.err.nameRequired"),
        },
        isLetter: {
          message: t("team.err.letters"),
        },
        min: {
          message: t("team.err.length"),
          value: 3,
        },
        checkName: {
          message: t("team.err.exists"),
          value: team,
        },
        max: {
          message: t("team.err.tooLong"),
          value: 32,
        },
      },
      memberTwo: {
        isRequired: {
          message: t("team.err.inbRequired"),
        },
        checkMember: {
          message: t("team.err.noUser"),
          value: memberTwo,
        },
        checkGroup: {
          message: t("team.err.inGroup"),
          value: memberTwo?.teamId || "",
        },
        checkInvitation: {
          message: t("team.err.invited"),
          value: invitationTwo,
        },
        isCurrentUser: {
          message: t("team.err.self"),
          value: currentUser?._id,
        },
        isRepeatUser: {
          message: t("team.err.repeat"),
          value: memberThree?._id,
        },
        max: {
          message: t("team.err.inbMax"),
          value: 25,
        },
      },
      memberThree: {
        isRequired: {
          message: t("team.err.inbRequired"),
        },
        checkMember: {
          message: t("team.err.noUser"),
          value: memberThree,
        },
        checkGroup: {
          message: t("team.err.inGroup"),
          value: memberThree?.teamId || "",
        },
        checkInvitation: {
          message: t("team.err.invited"),
          value: invitationThree,
        },
        isCurrentUser: {
          message: t("team.err.self"),
          value: currentUser?._id,
        },
        isRepeatUser: {
          message: t("team.err.repeat"),
          value: memberTwo?._id,
        },
        max: {
          message: t("team.err.inbMax"),
          value: 25,
        },
      },
      town: {
        isRequiredTown: {
          message: t("team.err.town"),
        },
      },
    }),
    [currentUser, memberTwo, memberThree, invitationThree, invitationTwo, team, t]
  );

  useEffect(() => {
    if (!currentUser) return;
    setData((prev) => ({
      ...prev,
      memberOne: currentUser._id,
      town: prev.town || currentUser.town || "",
    }));
  }, [currentUser]);

  const handleChange = (event) => {
    const value =
      event.name === "teamname" ? event.value : event.value.trim();
    setData((prevState) => ({
      ...prevState,
      [event.name]: value,
    }));
  };

  const validate = useCallback(() => {
    const errors = validator(data, validatorConfig);
    setErrors(errors);
    return Object.keys(errors).length === 0;
  }, [data, validatorConfig]);

  useEffect(() => {
    validate();
  }, [validate]);

  const isValid = Object.keys(errors).length === 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (block) return;

    const formIsValid = validate();
    if (!formIsValid || !currentUser || !memberTwo?._id || !memberThree?._id) {
      return;
    }

    setSubmitError("");
    const newData = {
      teamname: data.teamname.trim().toLowerCase(),
      status: "build",
      members: [
        { member: data.memberOne, accept: true },
        { member: memberTwo._id, accept: false },
        { member: memberThree._id, accept: false },
      ],
      payment: "unpaid",
      missionId: null,
      playId: null,
      town: data.town,
    };
    const created = await dispatch(createGroup({ newData, currentUser }));
    if (!created) {
      setSubmitError(t("team.fail"));
      return;
    }
    if (!localStorageService.getGroupId()) {
      localStorage.setItem("group-local-id", created._id);
    }
    setBlock(true);
    onShowModal("create", true);
  };

  return (
    <>
      {!groupsLoaded && groupsLoading ? (
        <p className="text-center py-8 text-slate-500">{t("common.loadingTeam")}</p>
      ) : group === undefined ? (
        <div className="relative min-w-0 flex-auto px-1 sm:px-4">
          {/* Информационный блок */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-slate-600 text-base leading-relaxed">
              <span className="font-semibold block mb-2">ℹ️ {t("team.infoLabel")}</span>
              {t("team.info")}
            </p>
          </div>

          {/* Город */}
          <div className="flex flex-row justify-center items-center bg-gray-50 rounded-lg p-3 mb-4">
            <span className="text-gray-600 font-medium mr-2">{t("common.city")}</span>
            <span className="text-gray-800 font-semibold">{town?.name}</span>
          </div>

          {errors.town ? (
            <div className="h-5 text-red-400 text-sm text-center mb-2">
              {errors.town}
            </div>
          ) : (
            <div className="h-5 mb-2"></div>
          )}

          {/* Форма */}
          <form onSubmit={handleSubmit} className="flex flex-col space-y-4">
            {/* Team Name */}
            <div className="bg-white rounded-lg p-3 shadow-sm">
              <TextField
                label={t("team.name")}
                name="teamname"
                placeholder={t("team.namePh")}
                className="w-full bg-gray-50 text-base py-3 px-4"
                value={data.teamname}
                onChange={handleChange}
                error={errors.teamname}
              />
            </div>

            {/* Первый рекрут */}
            <div className="bg-white rounded-lg p-3 shadow-sm">
              <TextField
                label={t("team.member1")}
                type="text"
                name="memberTwo"
                value={data.memberTwo}
                onChange={handleChange}
                className="w-full bg-gray-50 text-base py-3 px-4"
                placeholder={t("team.inbPh")}
                error={errors.memberTwo}
              />
            </div>

            {/* Второй рекрут */}
            <div className="bg-white rounded-lg p-3 shadow-sm">
              <TextField
                label={t("team.member2")}
                type="text"
                name="memberThree"
                value={data.memberThree}
                onChange={handleChange}
                className="w-full bg-gray-50 text-base py-3 px-4"
                placeholder={t("team.inbPh")}
                error={errors.memberThree}
              />
            </div>

            {submitError ? (
              <p className="text-red-500 text-sm text-center">{submitError}</p>
            ) : null}

            {/* Кнопка отправки */}
            <button
              type="submit"
              disabled={!isValid || block}
              className={`
              btn-smooth w-full py-4 px-6 rounded-xl font-bold text-lg
              shadow-lg hover:shadow-xl
              disabled:opacity-50 disabled:cursor-not-allowed
              disabled:transform-none disabled:shadow-none
              ${
                !isValid || block
                  ? "bg-gray-400 text-gray-200"
                  : "bg-gradient-to-r from-lime-500 to-green-500 text-white hover:from-lime-600 hover:to-green-600"
              }
            `}
            >
              {block ? t("team.sent") : t("team.invite")}
            </button>
          </form>

          {/* Мобильные подсказки */}
          <div className="mt-6 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800 text-center">
              {t("team.inbHint")}
            </p>
          </div>
        </div>
      ) : (
        <div className="relative px-4 md:px-10 py-8 flex-auto">
          <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
            <p className="text-green-800 text-2xl md:text-xl font-semibold mb-2">
              {t("team.already", { name: currentUser?.nickname })}
            </p>
            <p className="text-green-600 text-xl md:text-md">
              {t("team.cannot")}
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default CreateTeamPage;
